import { Component, ElementRef, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { APPLICATION_TYPE_LABELS, ApplicationType, ProjectFormData, SUPPORT_TYPE_OPTIONS } from 'app/models/application';
import { TOPIC_GROUPS, TopicGroup } from 'app/models/epe-topics';
import { ApplicationsService } from 'app/services/applications.service';
import { SessionStorage } from 'app/storage/session.storage';
import { switchMap } from 'rxjs/operators';
import { FieldError, focusAndScrollToField } from 'app/shared/forms/form-validation.util';

interface SelectedTopic { value: string; label: string; groupLabel: string; }

@Component({
  selector: 'app-project-application-form',
  templateUrl: './project-application-form.component.html',
  styleUrls: ['./project-application-form.component.scss']
})
export class ProjectApplicationFormComponent implements OnInit {
  form!: FormGroup;
  submitting    = false;
  formSubmitted = false;
  errorMessage  = '';
  fieldErrors: FieldError[] = [];
  serviceType!: ApplicationType;
  serviceLabel  = '';

  draftId    : string | null = null;
  savingDraft = false;
  draftSaved  = false;

  selectedTopics: SelectedTopic[] = [];
  expandedGroups    = new Set<string>();
  expandedSubGroups = new Set<string>();

  selectedInformation: string[] = [];

  readonly MAX_TOPICS = 3;
  readonly TOPIC_GROUPS: TopicGroup[] = TOPIC_GROUPS;

  readonly EU_FUNDING_PROGRAMMES: string[] = [
    'Horizon Europe',
    'Horizon 2020',
    'LIFE',
    'Digital Europe',
    'Other',
  ];

  readonly SUPPORT_OPTIONS: string[] = [...SUPPORT_TYPE_OPTIONS, 'Other'];

  readonly SUPPORT_START_OPTIONS: string[] = [
    'As soon as possible',
    'Within 1 month',
    'Within 3 months',
    'Within 6 months',
  ];

  readonly COUNTRIES: string[] = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
    'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
    'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
    'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana',
    'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
    'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic',
    'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Democratic Republic)',
    'Congo (Republic)', 'Costa Rica', "Côte d'Ivoire", 'Croatia', 'Cuba', 'Cyprus',
    'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea',
    'Estonia', 'Eswatini', 'Ethiopia',
    'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
    'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
    'Haiti', 'Honduras', 'Hungary',
    'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Jamaica', 'Japan', 'Jordan',
    'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya',
    'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
    'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
    'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
    'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua',
    'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
    'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay',
    'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar',
    'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
    'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
    'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
    'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
    'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
    'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
    'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
    'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen',
    'Zambia', 'Zimbabwe',
  ];

  readonly SDO_OPTIONS: { group: string; options: string[] }[] = [
    { group: 'European', options: [
      'ASD-STAN',
      'CEN – European Committee for Standardisation',
      'CENELEC – The European Electronic Committee for Standardisation',
      'ETSI',
      'SBS - Small Business Standards',
    ]},
    { group: 'National Standards Bodies', options: [
      'Austria: ASI – Austrian Standards Institute',
      'Austria: OVE - Österreichischer Verband für Elektrotechnik',
      'Belgium: NBN – Bureau de Normalisation',
      'Bulgaria: BDS – Bulgarian Institute for Standardisation',
      'Croatia: HZN – Croation Standards Institute',
      'Cyprus: CYS – Cyprus Organisation for Standardisation',
      'Czech Republic: UNMZ – Czech Office for Standards, Metrology and Testing',
      'Denmark: DS – Dansk Standard',
      'Estonia: EVS – Estonian Centre for Standardisation',
      'Finland: SFS – Finnish Standards Association',
      'France: AFNOR – Association Francaise de normalization',
      'Germany: DIN – Deutsches Institut für Normung e.V.',
      'Germany: DKE - Deutsche Kommission Elektrotechnik Elektronik und Informationstechnik in DIN und VDE',
      'Greece: ELOT – Hellenic Organisation for Standardisation',
      'Hungary: MSZT - Magyar Szabványügyi Testület',
      'Iceland: IST – Icelandic Standards',
      'Ireland: NSAI – National Standards Authority of Ireland',
      'Israel: SII – Standards Institution of Israel',
      'Italy: CEI – Comitato Elettrotecnico Italiano',
      'Italy: UNI – Ente Nazionale Italiano di Unificazione',
      'Latvia: LVS – Standardisation, Accreditation and Metrology Centre, Latvian Standard',
      'Lithuania: LST – Lithuanian Standards Board',
      "Luxembourg: ILNAS – Institut luxembourgeois de la normalisation, de l'accréditation, de la sécurité et qualité des produits et services",
      'Malta: MCCAA – Malta Competition and Consumer Affairs Authority',
      'Moldova, Republic of: ISM – Institute for Standardisation of Moldova',
      'Montenegro: ISME – Institute for Standardisation of Montenegro',
      'Netherlands: NEN – Nederlands Normalisatie-institut',
      'North Macedonia: ISRM – Standardisation Institute of the Republic of Macedonia',
      'Norway: SON – Standards Norway',
      'Norway: NEK - Norsk Elektroteknisk Komite (The Norwegian Electrotechnical Committee)',
      'Poland: PKN – Polski Komitet Normalizacyjny (Polish Committee for Standardization)',
      'Portugal: IPQ - Instituto Português da Qualidade',
      'Romania: ASRO - Asociaţia de Standardizare din România (Romanian Standards Association)',
      'Slovakia: SOSMT – Slovak Office of Standards, Metrology and Testing',
      'Slovenia: SIST – Slovenian Institute for Standardisation',
      'Spain: UNE – Asociación Española de Normalización',
      'Sweden: SEK - Svensk Elstandard',
      'Sweden: SIS – Swedish Institute for Standards',
      'Switzerland: SNV – Swiss Association for Standardisation',
      'Switzerland: CES - Electrosuisse',
      'Turkey: TSE – Turkish Standards Institution',
      'Ukraine: DTSU - State Enterprise "Ukrainian Scientific, Research and Training Center on Standardization, Certification and Quality Problems"',
      'United Kingdom: BSI – British Standards Institution',
    ]},
    { group: 'International', options: [
      'DMTF',
      'IEC – International Electrotechnical Commission',
      'IEEE - Institute of Electrical and Electronics Engineers',
      'IETF - Internet Engineering Task Force',
      'ISO - International Organization for Standardization',
      'ITU - International Telecommunication Union',
      'OGF – Open Grid Forum',
      'OneM2M',
      'UN/CEFACT – Trade Facilitation and E-business',
      'W3C - World Wide Web Consortium',
      'Other – Please specify',
    ]},
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private applicationsService: ApplicationsService,
    private auth: SessionStorage,
    private router: Router,
    private hostRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.serviceType  = this.route.snapshot.params['type'] as ApplicationType;
    this.serviceLabel = APPLICATION_TYPE_LABELS[this.serviceType] || this.serviceType;

    this.form = this.fb.group({
      // Q1
      funding_type:               ['eu', Validators.required],
      project_acronym:            ['', Validators.required],
      full_project_name:          ['', Validators.required],
      funding_programme:          [''],
      other_funding_programme:    [''],
      national_programme_name:    [''],
      national_programme_country: [''],
      grant_agreement:            ['', Validators.required],
      project_call_topic_id:      ['', Validators.required],
      coordinator_country:        ['', Validators.required],
      project_website:            ['', Validators.required],
      project_officer_name:       ['', Validators.required],
      project_officer_email:      [''],
      // Q3
      project_short_description:  ['', Validators.required],
      why_the_project_applying:   ['', Validators.required],
      other_support_specification: [''],
      // Q4
      support_start:              ['', Validators.required],
      support_milestone:          [''],
      cluster_support:            ['', Validators.required],
      cluster_details:            [''],
      // Q5
      know_standards:             ['no', Validators.required],
      standards_details:          [''],
      standard_reference:         this.fb.array([]),
      // Q6
      preferred_languages:        ['', Validators.required],
      time_zone:                  ['', Validators.required],
      scheduling_constraints:     [''],
      // Q7
      website_publication:        [null, Validators.required],
      data_consent:               [false, Validators.requiredTrue],
      accuracy_declaration:       [false, Validators.requiredTrue],
    });

    this.form.get('know_standards')!.valueChanges.subscribe(val => {
      if (val === 'yes' && this.standardReference.length === 0) {
        this.addStandardReference();
      }
      if (val === 'no') {
        while (this.standardReference.length > 0) {
          this.standardReference.removeAt(0);
        }
      }
    });

    const editId = this.route.snapshot.params['id'];
    if (editId) {
      this.loadExistingApplication(editId);
    }
  }

  private loadExistingApplication(id: string): void {
    this.applicationsService.getById(id).subscribe({
      next: app => {
        if (app.status !== 'draft') {
          this.router.navigate(['/my-applications']);
          return;
        }
        this.draftId = id;
        this.loadDraft(app.form_data as any || {});
      },
      error: () => this.router.navigate(['/my-applications'])
    });
  }

  private resolveTopicMeta(value: string): { label: string; groupLabel: string } | null {
    for (const group of TOPIC_GROUPS) {
      for (const sub of group.subGroups) {
        if (sub.value === value) return { label: sub.label, groupLabel: group.label };
        const child = sub.children.find(c => c.value === value);
        if (child) return { label: child.label, groupLabel: group.label };
      }
    }
    return null;
  }

  private loadDraft(data: any): void {
    // Populate standardReference BEFORE patchValue so the know_standards
    // valueChanges subscription sees length > 0 and won't add an extra empty row.
    if (data.know_standards === 'yes' && Array.isArray(data.standard_reference) && data.standard_reference.length > 0) {
      while (this.standardReference.length > 0) this.standardReference.removeAt(0);
      for (const ref of data.standard_reference as any[]) {
        this.standardReference.push(this.fb.group({
          sdo:           [ref.sdo || ''],
          tc_number:     [ref.tc_number || ''],
          tc_title:      [ref.tc_title || ''],
          other_options: [ref.other_options || ''],
        }));
      }
    }
    this.form.patchValue({
      funding_type:               data.funding_type ?? 'eu',
      project_acronym:            data.project_acronym ?? '',
      full_project_name:          data.full_project_name ?? '',
      funding_programme:          data.funding_programme ?? '',
      other_funding_programme:    data.other_funding_programme ?? '',
      national_programme_name:    data.national_programme_name ?? '',
      national_programme_country: data.national_programme_country ?? '',
      grant_agreement:            data.grant_agreement ?? '',
      project_call_topic_id:      data.project_call_topic_id ?? '',
      coordinator_country:        data.coordinator_country ?? '',
      project_website:            data.project_website ?? '',
      project_officer_name:       data.project_officer_name ?? '',
      project_officer_email:      data.project_officer_email ?? '',
      project_short_description:  data.project_short_description ?? '',
      why_the_project_applying:   data.why_the_project_applying ?? '',
      other_support_specification: data.other_support_specification ?? '',
      support_start:              data.support_start ?? '',
      support_milestone:          data.support_milestone ?? '',
      cluster_support:            data.cluster_support ?? '',
      cluster_details:            data.cluster_details ?? '',
      know_standards:             data.know_standards ?? 'no',
      standards_details:          data.standards_details ?? '',
      preferred_languages:        data.preferred_languages ?? '',
      time_zone:                  data.time_zone ?? '',
      scheduling_constraints:     data.scheduling_constraints ?? '',
      website_publication:        data.website_publication ?? null,
      data_consent:               data.data_consent ?? false,
      accuracy_declaration:       data.accuracy_declaration ?? false,
    });
    for (const topicValue of (data.topics || []) as string[]) {
      const meta = this.resolveTopicMeta(topicValue);
      if (meta && this.selectedTopics.length < this.MAX_TOPICS) {
        this.selectedTopics.push({ value: topicValue, label: meta.label, groupLabel: meta.groupLabel });
      }
    }
    this.selectedInformation = [...(data.information_to_attain || [])];
  }

  private buildFormData(): ProjectFormData {
    const v = this.form.value;
    return {
      funding_type:               v.funding_type,
      project_acronym:            v.project_acronym,
      full_project_name:          v.full_project_name,
      funding_programme:          v.funding_type === 'eu' ? v.funding_programme : '',
      other_funding_programme:    v.other_funding_programme,
      national_programme_name:    v.funding_type === 'national' ? v.national_programme_name : '',
      national_programme_country: v.funding_type === 'national' ? v.national_programme_country : '',
      grant_agreement:            v.grant_agreement,
      project_call_topic_id:      v.project_call_topic_id,
      coordinator_country:        v.coordinator_country,
      project_website:            v.project_website,
      project_officer_name:       v.project_officer_name,
      project_officer_email:      v.project_officer_email,
      topics:                     this.selectedTopics.map(t => t.value),
      information_to_attain:      this.selectedInformation,
      other_support_specification: v.other_support_specification,
      project_short_description:  v.project_short_description,
      why_the_project_applying:   v.why_the_project_applying,
      support_start:              v.support_start,
      support_milestone:          v.support_milestone,
      cluster_support:            v.cluster_support,
      cluster_details:            this.clusterSelected ? v.cluster_details : '',
      know_standards:             v.know_standards,
      standards_details:          v.know_standards === 'yes' ? v.standards_details : '',
      standard_reference:         v.know_standards === 'yes' ? v.standard_reference : [],
      preferred_languages:        v.preferred_languages,
      time_zone:                  v.time_zone,
      scheduling_constraints:     v.scheduling_constraints,
      website_publication:        v.website_publication,
      data_consent:               v.data_consent,
      accuracy_declaration:       v.accuracy_declaration,
      related_doc_application:    [],
    };
  }

  saveDraft(): void {
    this.savingDraft = true;
    const formData = this.buildFormData();
    const obs$ = (this.draftId
      ? this.applicationsService.updateForm(this.draftId, formData)
      : this.applicationsService.createDraft(this.auth.userId!, this.serviceType, formData)) as any;
    obs$.subscribe({
      next: (res: any) => {
        if (!this.draftId && res?._id) {
          const raw: any = res._id;
          this.draftId = raw?.['$oid'] ?? String(raw);
        }
        this.draftSaved  = true;
        this.savingDraft = false;
        setTimeout(() => { this.draftSaved = false; }, 3000);
      },
      error: () => { this.savingDraft = false; }
    });
  }

  // ── Getters ───────────────────────────────────────────────────────────────────

  get isEuFunded(): boolean { return this.form.get('funding_type')?.value === 'eu'; }
  get knowsStandards(): boolean { return this.form.get('know_standards')?.value === 'yes'; }
  get isOtherSupportSelected(): boolean { return this.selectedInformation.includes('Other'); }
  get isOtherFundingProgramme(): boolean { return this.form.get('funding_programme')?.value === 'Other'; }
  get clusterSelected(): boolean {
    const v = this.form.get('cluster_support')?.value;
    return v === 'cluster_only' || v === 'both';
  }

  // ── Validation ────────────────────────────────────────────────────────────────

  isFormValid(): boolean {
    return this.getFieldErrors().length === 0;
  }

  getFieldErrors(): FieldError[] {
    const errors: FieldError[] = [];

    const simpleFields: [string, string][] = [
      ['funding_type', 'Funding type'],
      ['project_acronym', "Project's short name"],
      ['full_project_name', 'Full project title'],
    ];
    for (const [key, label] of simpleFields) {
      if (this.form.get(key)?.invalid) errors.push({ key, label });
    }

    if (this.isEuFunded && !this.form.get('funding_programme')?.value) {
      errors.push({ key: 'funding_programme', label: 'EU programme' });
    }
    if (!this.isEuFunded) {
      if (!this.form.get('national_programme_name')?.value) {
        errors.push({ key: 'national_programme_name', label: 'National/regional funding programme' });
      }
      if (!this.form.get('national_programme_country')?.value) {
        errors.push({ key: 'national_programme_country', label: 'Funding programme country' });
      }
    }

    const moreFields: [string, string][] = [
      ['grant_agreement', 'Grant agreement / project reference number'],
      ['project_call_topic_id', 'Call code'],
      ['coordinator_country', "Coordinating organisation's country"],
      ['project_website', "Project's website"],
      ['project_officer_name', 'Funding body officer name'],
    ];
    for (const [key, label] of moreFields) {
      if (this.form.get(key)?.invalid) errors.push({ key, label });
    }

    if (this.selectedTopics.length === 0) {
      errors.push({ key: 'topics', label: 'Open call topic(s)' });
    }
    if (this.selectedInformation.length === 0) {
      errors.push({ key: 'information_to_attain', label: 'Type of standardisation support requested' });
    }

    const finalFields: [string, string][] = [
      ['project_short_description', "Project's objectives, context and expected results"],
      ['why_the_project_applying', 'Why standardisation matters for the project'],
      ['support_start', 'When support should start'],
      ['cluster_support', 'Individual project or cluster of projects'],
      ['preferred_languages', 'Preferred language(s) for support'],
      ['time_zone', 'Time zone'],
      ['website_publication', 'Public profile publication consent'],
      ['data_consent', 'Data processing consent'],
      ['accuracy_declaration', 'Accuracy declaration'],
    ];
    for (const [key, label] of finalFields) {
      if (this.form.get(key)?.invalid) errors.push({ key, label });
    }

    // Safety net: surface any invalid control this method didn't explicitly label.
    if (this.form.invalid && errors.length === 0) {
      for (const key of Object.keys(this.form.controls)) {
        if (this.form.get(key)?.invalid) errors.push({ key, label: key });
      }
    }

    return errors;
  }

  jumpToField(key: string): void {
    focusAndScrollToField(this.hostRef.nativeElement, key);
  }

  showEuFundingError(): boolean {
    return this.formSubmitted && this.isEuFunded && !this.form.get('funding_programme')?.value;
  }

  showNationalNameError(): boolean {
    return this.formSubmitted && !this.isEuFunded && !this.form.get('national_programme_name')?.value;
  }

  showNationalCountryError(): boolean {
    return this.formSubmitted && !this.isEuFunded && !this.form.get('national_programme_country')?.value;
  }

  // ── Topic picker ──────────────────────────────────────────────────────────────

  toggleGroup(label: string): void {
    this.expandedGroups.has(label) ? this.expandedGroups.delete(label) : this.expandedGroups.add(label);
  }
  isGroupExpanded(label: string): boolean { return this.expandedGroups.has(label); }

  toggleSubGroup(value: string): void {
    this.expandedSubGroups.has(value) ? this.expandedSubGroups.delete(value) : this.expandedSubGroups.add(value);
  }
  isSubGroupExpanded(value: string): boolean { return this.expandedSubGroups.has(value); }

  selectTopic(value: string, label: string, groupLabel: string): void {
    if (this.isTopicSelected(value)) {
      this.selectedTopics = this.selectedTopics.filter(t => t.value !== value);
    } else if (this.selectedTopics.length < this.MAX_TOPICS) {
      this.selectedTopics.push({ value, label, groupLabel });
    }
  }
  isTopicSelected(value: string): boolean { return this.selectedTopics.some(t => t.value === value); }
  removeTopic(value: string): void { this.selectedTopics = this.selectedTopics.filter(t => t.value !== value); }
  canSelectMore(): boolean { return this.selectedTopics.length < this.MAX_TOPICS; }

  // ── Support type ──────────────────────────────────────────────────────────────

  toggleInformation(option: string): void {
    const idx = this.selectedInformation.indexOf(option);
    if (idx >= 0) {
      this.selectedInformation.splice(idx, 1);
    } else {
      this.selectedInformation.push(option);
    }
  }
  isInformationSelected(option: string): boolean { return this.selectedInformation.includes(option); }

  // ── Standard reference FormArray ──────────────────────────────────────────────

  get standardReference(): FormArray {
    return this.form.get('standard_reference') as FormArray;
  }

  addStandardReference(): void {
    if (this.standardReference.length >= 1) return;
    this.standardReference.push(this.fb.group({
      sdo:           [''],
      tc_number:     [''],
      tc_title:      [''],
      other_options: [''],
    }));
  }

  isOtherSdo(refGroup: AbstractControl): boolean {
    return refGroup.get('sdo')?.value === 'Other – Please specify';
  }

  // ── Submit ────────────────────────────────────────────────────────────────────

  onSubmit(): void {
    this.formSubmitted = true;
    this.fieldErrors = this.getFieldErrors();
    if (this.fieldErrors.length) {
      focusAndScrollToField(this.hostRef.nativeElement, this.fieldErrors[0].key);
      return;
    }
    this.submitting = true;
    const formData = this.buildFormData();
    if (this.draftId) {
      this.applicationsService.updateForm(this.draftId, formData).pipe(
        switchMap(() => this.applicationsService.updateStatus(this.draftId!, 'pending', this.auth.userId!))
      ).subscribe({
        next: () => this.router.navigate(['/my-applications']),
        error: (err: any) => {
          this.errorMessage = err?.error?.message || 'An error occurred whilst submitting. Please try again.';
          this.submitting = false;
        }
      });
    } else {
      this.applicationsService.create(this.auth.userId!, this.serviceType, formData).subscribe({
        next: () => this.router.navigate(['/my-applications']),
        error: (err: any) => {
          this.errorMessage = err?.error?.message || 'An error occurred whilst submitting. Please try again.';
          this.submitting = false;
        }
      });
    }
  }
}
